import React, { useEffect, useState } from "react";
import { Card, Statistic, Row, Col, Tag, Spin, Button, Space, Typography } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    DatabaseOutlined,
    CloudServerOutlined,
    RocketOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import { getSystemHealthApi, ISystemHealth } from "api/system";
import { useNotificationContext } from "contexts/NotificationContext";
import moment from "moment";

const { Title, Text } = Typography;

export default function SystemHealth() {
    const { showError } = useNotificationContext();
    const [loading, setLoading] = useState(false);
    const [healthData, setHealthData] = useState<ISystemHealth | null>(null);

    const fetchHealth = async () => {
        setLoading(true);
        try {
            const { data, error } = await getSystemHealthApi();
            if (data) {
                setHealthData(data);
            } else {
                showError(error?.message || "Failed to fetch system health");
            }
        } catch (err) {
            showError("Failed to fetch system health");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHealth();
        // Refresh every 30 seconds
        const interval = setInterval(fetchHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (status: string) => {
        return status === "healthy" ? "#52c41a" : "#f5222d";
    };

    const getStatusTag = (status: string) => {
        return (
            <Tag color={status === "healthy" ? "success" : "error"} icon={status === "healthy" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                {status.toUpperCase()}
            </Tag>
        );
    };

    return (
        <div className="w-full p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <Title level={2}>System Health & Monitoring</Title>
                    <Text type="secondary">Real-time status of backend services and infrastructure</Text>
                </div>
                <Space>
                    {healthData && (
                        <Text type="secondary">
                            Last updated: {moment(healthData.timestamp).format("HH:mm:ss")}
                        </Text>
                    )}
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchHealth}
                        loading={loading}
                    >
                        Refresh Now
                    </Button>
                </Space>
            </div>

            {loading && !healthData ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} md={24}>
                            <Card className="shadow-sm">
                                <Statistic
                                    title="Overall System Status"
                                    value={healthData?.status?.toUpperCase() || "UNKNOWN"}
                                    valueStyle={{ color: getStatusColor(healthData?.status || "") }}
                                    prefix={healthData?.status === "healthy" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Title level={4} className="mb-4">Core Services</Title>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                            <Card className="shadow-sm" hoverable>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <DatabaseOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '12px' }} />
                                        <Title level={5}>PostgreSQL Database</Title>
                                        <Text type="secondary">Primary relational storage</Text>
                                    </div>
                                    {healthData && getStatusTag(healthData.services.database)}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="shadow-sm" hoverable>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <CloudServerOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '12px' }} />
                                        <Title level={5}>MongoDB</Title>
                                        <Text type="secondary">NoSQL document storage</Text>
                                    </div>
                                    {healthData && getStatusTag(healthData.services.mongodb)}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="shadow-sm" hoverable>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <RocketOutlined style={{ fontSize: '24px', color: '#eb2f96', marginBottom: '12px' }} />
                                        <Title level={5}>Redis Cache</Title>
                                        <Text type="secondary">In-memory data structure store</Text>
                                    </div>
                                    {healthData && getStatusTag(healthData.services.redis)}
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="mt-8 bg-gray-50 border-dashed">
                        <Text type="secondary">
                            <InfoCircleOutlined className="mr-2" />
                            This dashboard monitors connectivity and responsiveness of backend services. Alerts are automatically sent to the DevOps team if any service remains unhealthy for more than 5 minutes.
                        </Text>
                    </Card>
                </>
            )}
        </div>
    );
}
